import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmDelete } from '@/components/Ui/ConfirmDelete';
import { useUndoToast } from '@/components/Ui/Toast';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useRustyState } from '@/state';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

export function DeleteSection({ form, data }: Pick<SectionProps, 'form' | 'data'>) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const notify = useUndoToast();
  const { createLanguageURL } = useLanguageAwareRouting();
  const total = data.summary?.messages_total;
  const description =
    typeof total === 'number' && total > 0
      ? t('form.delete.withMessages', { count: total })
      : typeof total === 'number'
        ? t('form.delete.noMessages')
        : t('form.delete.unknown');

  const remove = async () => {
    const state = useRustyState.getState();
    await state.api.deleteForm(form.id);
    void state.refreshFormsSummary();
    void state.refreshProblemsCount();
    void state.refreshUnreadCount();
    notify(t('form.delete.done', { title: form.title }));
    nav(createLanguageURL('/account/forms'));
  };

  return (
    <section className={classes.danger} aria-label={t('form.delete.title')}>
      <div className={classes.dangerText}>
        <strong>{t('form.delete.title')}</strong>
        <span className={classes.sub}>{description}</span>
      </div>
      <ConfirmDelete
        label={t('form.delete.button')}
        description={t('form.delete.confirm', { title: form.title, details: description })}
        confirmLabel={t('form.delete.confirmButton')}
        onConfirm={remove}
      />
    </section>
  );
}
